import { Extension, Paragraph } from 'tiptap';
import { Heading } from 'tiptap-extensions';

/**
 * TipTap v1 has no alignment extension, so block alignment is added here as a
 * `textAlign` attribute on paragraphs and headings. It is written out as
 * `style="text-align:…"` (and parsed back from the same place), which is the
 * exact shape `normaliseQuillHtml` produces for legacy `ql-align-*` markup.
 *
 * Any *other* inline style on the block is carried through untouched in an
 * `extraStyle` attribute. Without it the normaliser's `margin-left:…em`
 * (converted from legacy `ql-indent-*`) would be silently dropped the first
 * time a promo was opened and saved.
 *
 * Nothing here may import `tiptap-commands` / `prosemirror-*`: only `tiptap`
 * and `tiptap-extensions` are declared dependencies of groovesell-ui, and the
 * rest are only reachable by accident through npm hoisting. The block-type
 * change therefore goes through `state.tr.setBlockType(...)`, a plain
 * prosemirror-transform method that needs no import at all.
 */
export const ALIGNMENTS = ['left', 'center', 'right', 'justify'];

const alignmentAttrs = {
  textAlign: { default: null },
  extraStyle: { default: null },
};

function readAlignment(dom) {
  const value = (dom.style && dom.style.textAlign)
    || dom.getAttribute('data-text-align')
    || null;

  return ALIGNMENTS.indexOf(value) === -1 ? null : value;
}

// Everything in the style attribute except `text-align`, which we own.
function readExtraStyle(dom) {
  const style = dom.getAttribute('style');
  if (!style) return null;

  const kept = style
    .split(';')
    .map(declaration => declaration.trim())
    .filter(declaration => declaration
      && declaration.split(':')[0].trim().toLowerCase() !== 'text-align')
    .join(';');

  return kept || null;
}

function alignmentDomAttrs({ textAlign, extraStyle }) {
  const style = [extraStyle, textAlign ? `text-align:${textAlign}` : '']
    .filter(Boolean)
    .join(';');

  return style ? { style } : {};
}

// The alignment/extra style of the node a block command should inherit.
function carriedAttrs(node) {
  const attrs = (node && node.attrs) || {};
  return {
    textAlign: attrs.textAlign || null,
    extraStyle: attrs.extraStyle || null,
  };
}

function parentBlock(state) {
  const { $from } = state.selection;
  return $from ? $from.parent : null;
}

function applyBlockType(state, dispatch, type, extraAttrs) {
  const { from, to } = state.selection;
  const tr = state.tr.setBlockType(from, to, type, node => ({
    ...carriedAttrs(node),
    ...extraAttrs,
  }));

  if (!tr.steps.length) return false;
  if (dispatch) dispatch(tr.scrollIntoView());

  return true;
}

export class AlignedParagraph extends Paragraph {
  get schema() {
    const base = super.schema;

    return {
      ...base,
      attrs: { ...(base.attrs || {}), ...alignmentAttrs },
      parseDOM: [
        {
          tag: 'p',
          getAttrs: dom => ({
            textAlign: readAlignment(dom),
            extraStyle: readExtraStyle(dom),
          }),
        },
      ],
      toDOM: node => ['p', alignmentDomAttrs(node.attrs), 0],
    };
  }

  commands({ type }) {
    // Keeps the alignment and any other inline style when a heading is turned
    // back into a paragraph.
    return () => (state, dispatch) => applyBlockType(state, dispatch, type);
  }
}

export class AlignedHeading extends Heading {
  get schema() {
    const base = super.schema;

    return {
      ...base,
      attrs: { ...(base.attrs || {}), ...alignmentAttrs },
      parseDOM: this.options.levels.map(level => ({
        tag: `h${level}`,
        getAttrs: dom => ({
          level,
          textAlign: readAlignment(dom),
          extraStyle: readExtraStyle(dom),
        }),
      })),
      toDOM: node => [
        `h${node.attrs.level}`,
        alignmentDomAttrs(node.attrs),
        0,
      ],
    };
  }

  commands({ type, schema }) {
    // The stock Heading command uses `toggleBlockType`, which relies on
    // `nodeIsActive(state, type, attrs)`. In tiptap v1 that helper compares a
    // node against its own attributes, so it reports "already active" for any
    // level and switching H1 -> H2 silently drops back to a paragraph.
    return (attrs = {}) => (state, dispatch) => {
      const block = parentBlock(state);
      const isSameHeading = Boolean(
        block && block.type === type && block.attrs.level === attrs.level
      );

      return isSameHeading
        ? applyBlockType(state, dispatch, schema.nodes.paragraph)
        : applyBlockType(state, dispatch, type, { level: attrs.level });
    };
  }
}

export class Alignment extends Extension {
  get name() {
    return 'alignment';
  }

  get defaultOptions() {
    return { types: ['paragraph', 'heading'] };
  }

  commands({ schema }) {
    return (attrs = {}) => (state, dispatch) => {
      // `left` is the default, so it is stored as `null` and no inline style
      // is emitted - matching what `normaliseQuillHtml` produces.
      const requested = attrs.textAlign;
      const textAlign = ALIGNMENTS.indexOf(requested) === -1 || requested === 'left'
        ? null
        : requested;
      const types = (this.options.types || [])
        .map(name => schema.nodes[name])
        .filter(Boolean);
      const { from, to } = state.selection;

      let tr = state.tr;
      let changed = false;

      state.doc.nodesBetween(from, to, (node, pos) => {
        if (types.indexOf(node.type) === -1) return;
        if (node.attrs.textAlign === textAlign) return;
        tr = tr.setNodeMarkup(pos, null, { ...node.attrs, textAlign });
        changed = true;
      });

      if (!changed) return false;
      if (dispatch) dispatch(tr.scrollIntoView());

      return true;
    };
  }
}

export default Alignment;
